import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from "@angular/common/http";
import {AuthService} from "../services/auth.service";
import {catchError, filter, tap} from "rxjs/operators";
import {Observable} from "rxjs";
import {Injectable} from "@angular/core";
import {UnauthorizedError} from "../models/errors/unauthorized.error";

/**
 * Interceptor to manage authorizations
 *
 * @author rodrigo@kissolutions.tech
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

  constructor(private authService: AuthService) {
  }

  /**
   * Intercepts requests to append `Authorization` header params and validate results
   * Also invalidates session
   *
   * @param req - intercepted request
   * @param next - next interceptor to call
   *
   * @returns response observable
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Some endpoints may not need interceptors
    if (req.headers.get('X-Ignore-Interceptors') === 'true') {
      return next.handle(req);
    }

    // Some endpoints may not need this interceptor
    if (req.headers.get('X-Ignore-AuthInterceptor') === 'true') {
      // We need to remove this header to prevent server side error (this is the last interceptor executed)
      req = req.clone({
        headers: req.headers.delete('X-Ignore-AuthInterceptor')
      });
      return next.handle(req);
    }

    // Request
    if (this.authService.loggedIn) {
      const key = `Bearer ${this.authService.token}`;
      req = req.clone({
        headers: req.headers.set('Authorization', key)
      });

      req = req.clone({
        withCredentials: true
      });
    }

    // Response
    return next.handle(req).pipe(
      // Skip `sent` event
      filter(res => res.type !== 0),
      tap((res: HttpResponse<any>) => {
        if (res instanceof HttpResponse) {
          if (res.body && res.body.header) {
            if (res.body.header.status === 401) {
              // Unauthorized
              this.authService.logout();
              throw new UnauthorizedError('401: by Client');
            }
          }
        }
      }),
      catchError((error) => {
        if (error.status === 401) {
          // Unauthorized
          this.authService.logout();
          throw new UnauthorizedError('401: by Jujo');
        } else {
          throw error;
        }
      })
    );
  }
}
